import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useManagerUpcomingFixtures } from '@/hooks/useManagerUpcomingFixtures'
import { useTeams } from '@/hooks/useTeams'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { getApiSeasonYear } from '@/lib/season'
import { useSeason } from '@/context/SeasonContext'
import { getInformalName, getTeamLogo } from '@/lib/teams'
import { UpcomingFixtures } from './components'
import type { MatchdayFixtures, Fixture, Team } from './types'

interface CompetitionTeam {
  name: string
  logo: string
  code?: string
}

// Transform database fixture to design-os format
function transformTeam(teamName: string, teams?: CompetitionTeam[]): Team {
  const shortName = getInformalName(teamName)
  return {
    id: teamName.toLowerCase().replace(/\s+/g, '-'),
    shortName,
    fullName: teamName,
    logo: getTeamLogo(teamName, teams),
  }
}

export function UpcomingFixturesSection() {
  const { user, currentMembership, loadingMemberships } = useAuth()
  const { season } = useSeason()
  const apiYear = getApiSeasonYear(season)

  const { competitionId } = useLeagueConfig()
  const { data: fixturesData, isLoading, error } = useManagerUpcomingFixtures(season, competitionId)
  const { data: teamsData, isLoading: isLoadingTeams } = useTeams(apiYear)
  const teams = teamsData?.teams

  // Transform and group fixtures by matchday
  const matchdays: MatchdayFixtures[] = useMemo(() => {
    if (!fixturesData?.length) return []

    // Group by matchday
    const matchdayMap: Record<number, Fixture[]> = {}

    for (const dbFixture of fixturesData) {
      const fixture: Fixture = {
        id: dbFixture.id,
        homeTeam: transformTeam(dbFixture.home_team, teams),
        awayTeam: transformTeam(dbFixture.away_team, teams),
        kickoff: dbFixture.kickoff_time,
        venue: dbFixture.venue || '',
        homeOdds: dbFixture.home_win_odds || 0,
        drawOdds: dbFixture.draw_odds || 0,
        awayOdds: dbFixture.away_win_odds || 0,
      }

      if (!matchdayMap[dbFixture.matchday]) {
        matchdayMap[dbFixture.matchday] = []
      }
      matchdayMap[dbFixture.matchday].push(fixture)
    }

    // Convert to array and sort by matchday
    return Object.entries(matchdayMap)
      .map(([matchday, fixtures]) => ({
        matchday: parseInt(matchday, 10),
        fixtures: fixtures.sort((a, b) =>
          new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime()
        ),
      }))
      .sort((a, b) => a.matchday - b.matchday)
  }, [fixturesData, teams])

  // Loading state
  if (!user) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <p>Please log in to view fixtures.</p>
      </div>
    )
  }

  if (loadingMemberships || isLoading || isLoadingTeams) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  if (!currentMembership) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <p>Please select a league to view fixtures.</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-500 dark:text-red-400">
        <p>Failed to load fixtures. Please try again.</p>
      </div>
    )
  }

  return (
    <UpcomingFixtures
      matchdays={matchdays}
    />
  )
}
